import { COOKIES } from '@belong/constants';
import { getCookie, parseCookieValue } from '@belong/cookies';
import { logger } from '@belong/logging';
import type { INbnPersistedCookieData } from '@belong/nbn-utils';
import type { GetServerSidePropsContext, NextPageContext } from 'next';
import type { NextRouter } from 'next/router';

const isNdcReady = (cookieData: INbnPersistedCookieData): boolean => {
  const { isNdc, ndcPaymentOption, ndcProductCode } = cookieData;

  if (isNdc === false) {
    return true;
  }

  return Boolean(ndcPaymentOption && ndcProductCode);
};

const isAddressReady = (cookieData: INbnPersistedCookieData): boolean => {
  return Boolean(cookieData.address && cookieData.sqLastDoneAt);
};

const isModemSelectionReady = (cookieData: INbnPersistedCookieData): boolean => {
  const { modemSelection, modemPaymentOption, modemProductCode } = cookieData;

  if (!modemSelection) {
    return false;
  }

  if (modemSelection === 'byo') {
    return true;
  }

  if (modemSelection === 'belong') {
    return Boolean(modemPaymentOption && modemProductCode);
  }

  return false;
};

const isPlanSelectionReady = (cookieData: INbnPersistedCookieData): boolean => {
  const { productCode } = cookieData;

  return Boolean(productCode);
};

const parseNbnPersistentCookieData = (
  context: NextPageContext | GetServerSidePropsContext
): INbnPersistedCookieData | null => {
  const cookieValue = getCookie<string>(COOKIES.NBN_PERSISTENT_STORE, context, {
    parse: false
  });

  if (!cookieValue) {
    return null;
  }

  return parseCookieValue<INbnPersistedCookieData>(cookieValue);
};

const checkFunctions = {
  sq: isAddressReady,
  plan: isPlanSelectionReady,
  modem: isModemSelectionReady,
  ndc: isNdcReady
};

export const isNbnOrderSelectionReady = (cookieData: INbnPersistedCookieData | null): boolean => {
  if (!cookieData) {
    return false;
  }

  // For server-side resumption, we can retrieve order using prospectId
  if (cookieData.prospectId) {
    logger.info('Checking NBN order ready for resume: prospectId is present');
    return true;
  }

  const isReady = Object.entries(checkFunctions).every(([key, checkFunction]) => {
    const isStepReady = checkFunction(cookieData);

    if (!isStepReady) {
      logger.info(`Checking NBN order ready for resume: ${key} is not ready`);
    }

    return isStepReady;
  });

  return isReady;
};

export const isNbnOrderReadyForResume = (context: NextPageContext | GetServerSidePropsContext): boolean => {
  try {
    const cookieData = parseNbnPersistentCookieData(context);

    const isReadyForResume = isNbnOrderSelectionReady(cookieData);

    if (isReadyForResume) {
      logger.info('Checking NBN order ready for resume', { isReadyForResume });
    }

    return isReadyForResume;
  } catch (error) {
    logger.error('Failed to check if NBN order is ready for resume', error);
    return false;
  }
};

interface INbnOrderProspectContext {
  prospectId?: string;
  source?: string;
}

export const getNbnOrderProspectContext = (
  query: NextRouter['query'],
  nbnCookie?: INbnPersistedCookieData
): INbnOrderProspectContext => {
  const queryProspectId = query?.id;

  // If there is `id` in the URL query string (not an array), use it
  if (queryProspectId && !Array.isArray(queryProspectId)) {
    return {
      prospectId: queryProspectId,
      source: 'Email'
    };
  }

  // Try to get the prospectId from cookie
  const cookieProspectId = nbnCookie?.prospectId;

  if (cookieProspectId) {
    return {
      prospectId: cookieProspectId,
      source: 'Cart'
    };
  }

  return {};
};
